import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NavigationService } from '../../services/navigation/navigation.service';
import { CacheService } from '../../services/cache/cache.service';
import { CommonService } from '../../services/common/common.service';
import { Column } from '../interfaces/column.interface';
import moment from 'moment';
import { VisitsDataService } from './visits-data.service';
import { Visit, VisitsResult } from '../interfaces';
import { BannersDataService } from './banners-data.service';
import { Banner, BannersResult } from '../interfaces';
import { UsersDataService } from './users-data.service';
import { User, UsersResult } from '../interfaces';

@Injectable({
  providedIn: 'root'
})
export class ClicksColumnsService {

  public visits: Visit[] = [];
  public banners: Banner[] = [];
  public users: User[] = [];
  public defaultParameters: {[param: string]: string | string[]} = {offset: '0', limit: '1000'};

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private cache: CacheService,
    private common: CommonService,
    private visitsDataService: VisitsDataService,
    private bannersDataService: BannersDataService,
    private usersDataService: UsersDataService,

  ) {
    this.getVisits()
    this.getBanners()
    this.getUsers()
  }

  getVisits(offset: number = 0): void {
    const cacheKey: string = 'visits.cache'
    const cache: any = this.cache.get(cacheKey);
    if (cache !== null && offset === 0) {
      this.visits = cache.results
      return;
    }
    const parameters = Object.assign(this.defaultParameters, {offset: offset + ''});
    this.visitsDataService.getVisits(parameters).subscribe((response: VisitsResult|any) => {
      if (response !== null) {
        if (offset === 0) {
          this.visits = response.results;
        } else {
          this.visits = this.visits.concat(response.results);
        }
        if (response.inlineCount > this.visits.length) {
          offset = offset + parseInt(this.defaultParameters.limit + '', 10);
          this.getVisits(offset);
        }
        response.results = this.visits;
        this.cache.set(cacheKey, response);
      }
    });
  }

  getBanners(offset: number = 0): void {
    const cacheKey: string = 'banners.cache'
    const cache: any = this.cache.get(cacheKey);
    if (cache !== null && offset === 0) {
      this.banners = cache.results
      return;
    }
    const parameters = Object.assign(this.defaultParameters, {offset: offset + ''});
    this.bannersDataService.getBanners(parameters).subscribe((response: BannersResult|any) => {
      if (response !== null) {
        if (offset === 0) {
          this.banners = response.results;
        } else {
          this.banners = this.banners.concat(response.results);
        }
        if (response.inlineCount > this.banners.length) {
          offset = offset + parseInt(this.defaultParameters.limit + '', 10);
          this.getBanners(offset);
        }
        response.results = this.banners;
        this.cache.set(cacheKey, response);
      }
    });
  }

  getUsers(offset: number = 0): void {
    const cacheKey: string = 'users.cache'
    const cache: any = this.cache.get(cacheKey);
    if (cache !== null && offset === 0) {
      this.users = cache.results
      return;
    }
    const parameters = Object.assign(this.defaultParameters, {offset: offset + ''});
    this.usersDataService.getUsers(parameters).subscribe((response: UsersResult|any) => {
      if (response !== null) {
        if (offset === 0) {
          this.users = response.results;
        } else {
          this.users = this.users.concat(response.results);
        }
        if (response.inlineCount > this.users.length) {
          offset = offset + parseInt(this.defaultParameters.limit + '', 10);
          this.getUsers(offset);
        }
        response.results = this.users;
        this.cache.set(cacheKey, response);
      }
    });
  }

  getColumns(): Column[] {
    return [
      { field: 'id', sortField: 'id', title: this.translate.instant('ID'), show: (this.navigation.getParamValue('clicks.id.showFiled', true) && true), displayInList: true, type: 'numericValue'},
      { field: 'visit', title: this.translate.instant('VISIT'), type: 'linkValue', filterData: this.visits, show: (this.navigation.getParamValue('clicks.visitId.showFiled', true) && true), displayInList: true, filterField: 'visit.id', displayField: 'ip', sortField: 'visit.ip', url: 'visits' },
      { field: 'banner', title: this.translate.instant('BANNER'), type: 'linkValue', filterData: this.banners, show: (this.navigation.getParamValue('clicks.bannerId.showFiled', true) && true), displayInList: true, filterField: 'banner.id', displayField: 'name', sortField: 'banner.name', url: 'banners' },
      { field: 'createdAt', sortField: 'createdAt', title: this.translate.instant('CREATEDAT'), show: (this.navigation.getParamValue('clicks.createdAt.showFiled', true) && true), displayInList: true, type: 'dateValue', valueFormatter: this.translate.instant('DATETIMEFORMAT')},
      { field: 'creatorUser', title: this.translate.instant('CREATORUSER'), type: 'linkValue', filterData: this.users, show: (this.navigation.getParamValue('clicks.creatorUserId.showFiled', true) && true), displayInList: true, filterField: 'creatorUser.id', displayField: 'username', sortField: 'creatorUser.username', url: 'users' },
      { field: 'actions', title: this.translate.instant('ACTIONS'), type: 'static', show: true, displayInList: true, sortField: null},
    ];
  }

  format(fieldName: string, value: any) {
    if (fieldName.toLowerCase().indexOf('time') > -1) {
      return moment(value).format('YYYY-MM-DD HH:mm');
    } else if (fieldName.toLowerCase().indexOf('date') > -1) {
      return moment(value).format('YYYY-MM-DD');
    }
    return value;
  }

}
